'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { useEffect, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Col, Row } from 'reactstrap';
import useWindowWidth from 'shared/hooks/useWindowWidth';

type Props = {
  title: JSX.Element;
  cards: JSX.Element[]; // MAKE SURE THAT THE PASSED CARDS HAVE AN ID LIKE THIS: `${id}-${index}`
  id: string;
  cardSteps?: number;
  cta?: JSX.Element;
  cardWidthPercentage?: number;
};

// scss: sandyou.be/stylesheets/components/carrousel.scss

export default function Carrousel({ title, cards, id, cardSteps = 1, cta, cardWidthPercentage }: Props) {
  const mobile = (useWindowWidth() ?? 0) < 768;
  const steps = mobile ? 1 : cardSteps;
  const cardsWithCta = cta ? [...cards, cta] : cards;

  const [card, setCard] = useState(0);
  const [carrouselElements, setCarrouselElements] = useState<HTMLElement[]>([]);
  const [walk, setWalk] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const offset = loaded ? carrouselElements[card]?.offsetLeft + walk : 0;

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);

  const cardAdj = Math.ceil(card / steps);
  const lastCard = Math.ceil(cards.length / steps) + (cta && mobile ? 1 : 0);
  const widthBar = 104;

  const handleDown = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const startPosX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    const startPosY = 'touches' in e ? e.touches[0].pageY : e.pageY;
    setStartX(startPosX);
    setStartY(startPosY);
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    if ('touches' in e) {
      document.body.style.overflow = 'hidden'; // Prevent full page scrolling on touch devices, but allow scrolling in the carrousel
      window.scrollBy({ top: startY - e.touches[0].pageY, behavior: 'instant' });
    }
    const currentX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    setWalk(startX - currentX);
  };

  const handleUp = () => {
    setIsDragging(false);
    document.body.style.overflow = '';
    if (walk > 10) {
      if (cardAdj === lastCard - 1) return setWalk(0);
      setCard(card + steps);
    }
    if (walk < -10) {
      if (cardAdj === 0) return setWalk(0);
      setCard(card - steps);
    }
    setWalk(0);
  };

  useEffect(() => {
    setCarrouselElements(
      cardsWithCta.map((_, index) => document.getElementById(`${id}-${index}`)).filter((el): el is HTMLElement => el !== null)
    );
    setLoaded(true);
  }, []);

  return (
    <Row className="carrouselComponent">
      {/* Title */}
      <Col xs={{ size: 12, order: 0 }} lg={{ size: 6, order: 0 }}>
        {title}
      </Col>
      <Col
        className="d-flex align-items-center justify-content-center justify-content-lg-end"
        xs={{ size: 12, order: 2 }}
        lg={{ size: 6, order: 1 }}
      >
        <Row className="mt-3 m-lg-0 z-1">
          {/* Progressbar */}
          <Col xs={{ order: 2, size: 'auto' }} lg={{ order: 1, size: 'auto' }} className="d-flex align-items-center gap-3">
            <div className="fs-7">{cardAdj + 1}</div>
            <div className="progress" style={{ width: `${widthBar}px`, height: '4px' }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${Math.round((1 / lastCard) * 100)}%`,
                  backgroundColor: '#2F3336',
                  transform: `translateX(${Math.round(((card / lastCard) * widthBar) / steps)}px)`,
                }}
              ></div>
            </div>
            <div className="fs-7">{lastCard}</div>
          </Col>
          {/* Button left */}
          <Col xs={{ order: 1, size: 'auto' }} lg={{ order: 2, size: 'auto' }}>
            <button
              onClick={() => {
                if (card > 0) setCard(card - steps);
              }}
              className="d-flex justify-content-center align-items-center bg-white p-0 border border-black rounded-circle"
              style={{ width: '40px', height: '40px' }}
            >
              <MdChevronLeft size={34} />
            </button>
          </Col>
          {/* Button right */}
          <Col xs={{ order: 3, size: 'auto' }} lg={{ order: 3, size: 'auto' }}>
            <button
              onClick={() => {
                if (cardAdj < lastCard - 1) setCard(card + 1 * steps);
              }}
              className="d-flex justify-content-center align-items-center bg-white p-0 border border-black rounded-circle"
              style={{ width: '40px', height: '40px' }}
            >
              <MdChevronRight size={34} />
            </button>
          </Col>
        </Row>
      </Col>
      <Col className="mt-4" xs={{ size: 12, order: 1 }} lg={{ size: 12, order: 2 }}>
        {/* Cards */}
        <div
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={handleUp}
          onMouseLeave={handleUp}
          onTouchStart={handleDown}
          onTouchMove={handleMove}
          onTouchEnd={handleUp}
          className={`card-deck my-2 d-flex flex-row flex-nowrap ${isDragging ? '' : 'smooth'}`}
          style={{ transform: `translateX(${-offset}px)` }}
        >
          {cards.map((c, index) => (
            <div
              style={{
                minWidth: mobile ? '100%' : cardWidthPercentage ? `${cardWidthPercentage}%` : 'max-content',
                minHeight: '100%',
              }}
              key={`${id}-${index}`}
              id={`${id}-${index}`}
            >
              {c}
            </div>
          ))}
          {cta && (
            <div
              style={{
                minWidth: mobile
                  ? '100%'
                  : cardWidthPercentage
                    ? `${97 - cardWidthPercentage < 20 ? 100 : 97 - cardWidthPercentage}%`
                    : 'max-content',
                minHeight: '100%',
              }}
            >
              {cta}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
}
